<template src="@/components/templates/signup.html"></template>

<script>
import FileUpload from "vue-upload-component"
import Pica from "pica"
import Cropper from "cropperjs"
import Datepicker from "vuejs-datepicker"
import PlayerUser from "@/models/PlayerUser"
import STATUS_CODES from "@/common/statusCodes"
import { TOASTER_SUCCESS, TOASTER_ERROR } from "@/common/events.types"
import moment from "moment"
import {
  TERMS_URL,
  PRIVACY_POLICY_URL,
  COPPA_POLICY_URL,
} from "@/constants/RouterConstants"
import { FETCH_SIGN_UP_DATA, SIGN_UP, EMAIL_CHECK, ADD_ADDITIONAL_EMAIL } from "@/store/action.types"
import { mapActions, mapGetters } from "vuex"
import _ from "lodash"
import * as helper from "@/common/helper"
import { en, fr } from "vuejs-datepicker/dist/locale"
import { UPDATE_LANGUAGE } from "@/store/action.types"
import intlTelInput from "intl-tel-input"
import "intl-tel-input/build/css/intlTelInput.css"
import TagInput from "@/components/common/form/TagInput.vue";

export default {
  name: "Signup",
  data() {
    return {
      files: [],
      edit: false,
      cropper: false,
      initialPlayerEmail: "",
      playerUser: new PlayerUser(),
      pageLoaded: false,
      linkIsInvalid: false,
      formSubmitted: false,
      TERMS_URL,
      PRIVACY_POLICY_URL,
      COPPA_POLICY_URL,
      emailValid: true,
      parentEmailValid: true,
      invalidLinkMessage: "",
      maxImageSize: 500,
      avatarLoading: false,
      resizingImageInProgress: false,
      formButtonLoading: false,
      redeemedLink: false,
      en: en,
      fr: fr,
      isMetric: true,
      feetInput: "",
      inchInput: "",
      phoneValid: null,
      parentPhoneValid: null,
      intlTelInputInitialized: false,
      intlTelInputInitializedParent: false,
      countryCode: null,
      parentPhoneNumber: "",
      parentCountryCode: "",
      additionalEmails: {
        emails: [],
        invalid: false
      },
      playerIdOnLoad: null
    }
  },
  computed: {
    ...mapGetters(["languageChangedOnSignUp", "user"]),
    signUpToken: {
      get() {
        return this.$route.params.token
      },
    },
    dayOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "D")
      },
      set(newDay) {
        this.playerUser.dateOfBirth = newDay
      },
    },
    monthOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "M")
      },
      set(newDay) {
        this.playerUser.monthOfBirth = newDay
      },
    },
    yearOfBirth: {
      get() {
        return moment(this.playerUser.dateOfBirth, "Y")
      },
      set(newDay) {
        this.playerUser.yearOfBirth = newDay
      },
    },
    parentalConsentNeeded: {
      get() {
        if (this.playerUser.lockedParent) {
          return true
        } else {
          let playersDateOfBirthUtc = moment.utc(this.playerUser.dateOfBirth)
          let currentDateUtc = moment.utc(new Date())
          let duration = moment.duration(
            currentDateUtc.diff(playersDateOfBirthUtc)
          )
          let yearsDiff = duration.asYears()

          return yearsDiff < 14
        }
      },
    },
  },
  mounted() {
    this.$i18n.locale = this.$route.query.lang

    this.fetchSignUpDataAction(this.signUpToken)
      .then((response) => {
        if (response.status.code === STATUS_CODES.BP_200) {
          this.playerUser = new PlayerUser(response.data)
          this.initialPlayerEmail = this.playerUser.email
          this.pageLoaded = true
          this.linkIsInvalid = false
          this.getPlayerIdOnLoad(response.data.playerId);
        } else if (response.status.code === STATUS_CODES.BP_412) {
          this.redeemedLink = true
          this.linkIsInvalid = false
        } else if (response.status.code === STATUS_CODES.BP_405) {
          this.linkIsInvalid = true
        } else if (response.status.code === STATUS_CODES.BP_404) {
          this.linkIsInvalid = true
        }
      })
      .catch((error) => {
        this.linkIsInvalid = true
        this.pageLoaded = true
      })
  },
  created() {
    this.debounceInput = _.debounce(($event) => {
      let inputElement = $event.target
      let inputElementId = inputElement.getAttribute("id")
      let role = inputElementId === "email" ? "PLAYER" : "PARENT"
      let email =
        inputElementId === "email"
          ? this.playerUser.email
          : this.playerUser.parentEmail

      this.checkEmail({
        token: this.signUpToken,
        credentials: {
          email,
          role,
        },
      })
        .then((data) => {
          if (role === "PLAYER") {
            this.emailValid = data
          } else {
            this.parentEmailValid = data
          }
        })
        .catch(() => {
          if (role === "PLAYER") {
            this.emailValid = false
          } else {
            this.parentEmailValid = false
          }
        })
    }, 500)
  },
  updated() {
    const context = this
    this.$nextTick(function() {
      const input = document.querySelector("#phoneNumber")
      if (!this.intlTelInputInitialized && !this.linkIsInvalid && input) {
        this.intlTelInputInitialized = true
        this.iti = intlTelInput(input, {
          utilsScript:
            "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
          initialCountry: context.playerUser.phone.countryCode || "us",
          preferredCountries: ["us", "ca", "gb", "de"],
          nationalMode: true,
          autoPlaceholder: "off",
          formatOnDisplay: false,
        })

        let countryData = context.iti.getSelectedCountryData()
        this.countryCode = countryData.iso2

        function handleCountryChange() {
          let changedCountryData = context.iti.getSelectedCountryData()
          context.countryCode = changedCountryData.iso2
          context.phoneValid = context.iti.isValidNumber() ? true : false
          if (context.phoneValid) {
            context.$validator.errors.removeById("invalidPhone")
          } else if (!context.phoneValid && !context.playerUser.phone.number) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        function handlePhoneChange() {
          context.phoneValid = context.iti.isValidNumber() ? true : false
          if (context.phoneValid) {
            context.$validator.errors.removeById("invalidPhone")
          } else if (!context.phoneValid && !context.playerUser.phone.number) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        input.addEventListener("countrychange", handleCountryChange)
        input.addEventListener("change", handlePhoneChange)
      }

      if (
        !this.intlTelInputInitializedParent &&
        !this.linkIsInvalid &&
        this.parentalConsentNeeded
      ) {
        this.intlTelInputInitializedParent = true
        const parentPhoneInput = document.querySelector("#parentPhoneNumber")
        this.parentIti = intlTelInput(parentPhoneInput, {
          utilsScript:
            "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
          initialCountry: context.playerUser.phone.countryCode || "us",
          preferredCountries: ["us", "ca", "gb", "de"],
          nationalMode: true,
          autoPlaceholder: "off",
          formatOnDisplay: false,
        })

        let countryData = context.iti.getSelectedCountryData()
        this.parentCountryCode = countryData.iso2

        function handleParentCountryChange() {
          let changedCountryData = context.parentIti.getSelectedCountryData()
          context.parentCountryCode = changedCountryData.iso2
          context.parentPhoneValid = context.parentIti.isValidNumber()
            ? true
            : false
          if (context.parentPhoneValid) {
            context.$validator.errors.removeById("invalidParentPhone")
          } else if (!context.parentPhoneValid && !context.parentPhoneNumber) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        function handleParentPhoneChange() {
          context.parentPhoneValid = context.parentIti.isValidNumber()
            ? true
            : false
          if (context.parentPhoneValid) {
            context.$validator.errors.removeById("invalidParentPhone")
          } else if (!context.parentPhoneValid && !context.parentPhoneNumber) {
            context.$validator.errors.removeById("invalidPhone")
          }
        }

        parentPhoneInput.addEventListener(
          "countrychange",
          handleParentCountryChange
        )
        parentPhoneInput.addEventListener("change", handleParentPhoneChange)
      }
    })
  },
  beforeDestroy() {
    this.iti.destroy()
    if (this.parentalConsentNeeded) {
      this.parentIti.destroy()
    }
  },
  watch: {
    edit(value) {
      const context = this

      if (value) {
        this.$nextTick(function() {
          context.avatarLoading = true

          if (!this.$refs.editImage) {
            return
          }

          function loaded() {
            context.avatarLoading = false
          }

          if (this.$refs.editImage.complete) {
            loaded()
          } else {
            this.$refs.editImage.addEventListener("load", loaded)
            this.$refs.editImage.addEventListener("error", function() {
              console.log("error")
            })
          }

          let cropper = new Cropper(this.$refs.editImage, {
            viewMode: 1,
          })

          this.cropper = cropper
        })
      } else {
        if (this.cropper) {
          this.cropper.destroy()
          this.cropper = false
        }
      }
    },
  },
  methods: {
    ...mapActions({
      fetchSignUpDataAction: FETCH_SIGN_UP_DATA,
      signUpAction: SIGN_UP,
      checkEmail: EMAIL_CHECK,
      updateLanguageAction: UPDATE_LANGUAGE,
      addAditionalEmailAction: ADD_ADDITIONAL_EMAIL
    }),
    editAndResize() {
      this.resizingImageInProgress = true
      const context = this

      setTimeout(function() {
        context.editSave()
      }, 100)
    },
    editSave() {
      const pica = Pica()
      this.edit = false
      let oldFile = this.files[0]
      let canvasImg = this.cropper.getCroppedCanvas()

      const resizedCanvas = document.createElement("canvas")
      resizedCanvas.height = 500
      resizedCanvas.width = 500

      pica.resize(canvasImg, resizedCanvas).then((res) => {
        let binStr = atob(res.toDataURL(oldFile.type).split(",")[1])
        let arr = new Uint8Array(binStr.length)
        for (let i = 0; i < binStr.length; i++) {
          arr[i] = binStr.charCodeAt(i)
        }
        let file = new File([arr], oldFile.name, { type: oldFile.type })
        this.$refs.upload.update(oldFile.id, {
          file,
          type: file.type,
          size: file.size,
          active: true,
        })
        this.playerUser.file = file
        this.resizingImageInProgress = false
      })
    },
    inputFile(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        this.$nextTick(() => {
          this.edit = true
        })
      }
      if (!newFile && oldFile) {
        this.edit = false
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          return prevent()
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.url = ""
        let URL = window.URL || window.webkitURL
        if (URL && URL.createObjectURL) {
          newFile.url = URL.createObjectURL(newFile.file)
        }
      }
    },
    onDaySelect(date) {
      this.playerUser.dateOfBirth = date
    },
    onMonthSelect(date) {
      let dayNumber = new Date(this.playerUser.dateOfBirth).getDate()
      let daysInMonth = helper.daysInMonth(
        date.getMonth() + 1,
        date.getFullYear()
      )

      if (dayNumber > daysInMonth) {
        this.playerUser.dateOfBirth = new Date(
          this.playerUser.dateOfBirth
        ).setDate(1)
      }

      this.playerUser.dateOfBirth = new Date(
        this.playerUser.dateOfBirth
      ).setMonth(date.getMonth())
    },
    onYearSelect(date) {
      let dayNumber = new Date(this.playerUser.dateOfBirth).getDate()
      let daysInMonth = helper.daysInMonth(
        date.getMonth() + 1,
        date.getFullYear()
      )

      if (dayNumber > daysInMonth) {
        this.playerUser.dateOfBirth = new Date(
          this.playerUser.dateOfBirth
        ).setDate(1)
      }

      this.playerUser.dateOfBirth = new Date(
        this.playerUser.dateOfBirth
      ).setYear(date.getFullYear())
    },

    convertMetricAndLog() {
      this.isMetric = !this.isMetric
      this.$amplitude.logEvent('pwa: change signup measurement unit', {unit: this.isMetric ? "METRIC" : "IMPERIAL"})
      if (this.isMetric) {
        // feet & inch to cm
        this.playerUser.height = Math.round(
          (Number(this.feetInput) * 12 + Number(this.inchInput)) * 2.54
        )
        if (this.playerUser.weight != null) {
          this.playerUser.weight = (this.playerUser.weight * 0.453592).toFixed(
            1
          )
        }
      } else {
        // cm to feet & inch
        this.feetInput = Math.floor((this.playerUser.height * 0.3937) / 12)
        this.inchInput = Math.round((this.playerUser.height * 0.3937) % 12)
        if (this.playerUser.weight != null) {
          this.playerUser.weight = (this.playerUser.weight / 0.453592).toFixed(
            2
          )
        }
      }
    },
    validateAndSubmit() {
      let context = this
      this.phoneValid =
        this.iti.isValidNumber() || !this.playerUser.phone.number ? true : false

      this.$validator.validateAll().then((result) => {
        if (!context.phoneValid && context.playerUser.phone.number) {
          this.$validator.errors.add({
            field: "phoneNumber",
            msg: "Invalid Phone number.",
            id: "invalidPhone",
          })
        }

        if (this.parentalConsentNeeded) {
          this.parentPhoneValid =
            this.parentIti.isValidNumber() || !this.parentPhoneNumber
              ? true
              : false

          if (!context.parentPhoneValid && context.parentPhoneNumber) {
            this.$validator.errors.add({
              field: "parentPhone",
              msg: "Invalid Phone number.",
              id: "invalidParentPhone",
            })
          }
        }

        this.formSubmitted = true

        if (
          this.emailValid &&
          this.parentEmailValid &&
          !this.resizingImageInProgress &&
          !this.avatarLoading &&
          this.phoneValid
        ) {
          let playerUser = this.playerUser
          let initialPlayerEmail = this.initialPlayerEmail
          let parentalConsentNeeded = this.parentalConsentNeeded
          playerUser.dateOfBirth = moment(playerUser.dateOfBirth).format(
            "YYYY-MM-DD"
          )

          if (!result || !playerUser.termsAndPolicyConfirmed) {
            this.formSubmitted = true
          } else if (
            parentalConsentNeeded &&
            !playerUser.legalGuardianConfirmed
          ) {
            this.formSubmitted = true
          } else if (parentalConsentNeeded && !context.parentPhoneValid) {
            this.formSubmitted = true
          } else {
            this.formButtonLoading = true
            let playerUserFormData = new FormData()
            let emailChanged = initialPlayerEmail !== playerUser.email

            if (parentalConsentNeeded) {
              emailChanged = initialPlayerEmail !== playerUser.parentEmail
            }

            if (!this.isMetric) {
              this.playerUser.height = Math.round(
                (this.feetInput * 12 +
                  ((this.playerUser.height * 0.3937) % 12)) *
                  2.54
              )
              if (this.playerUser.weight != null) {
                this.playerUser.weight = (
                  this.playerUser.weight * 0.453592
                ).toFixed(1)
              }
              this.isMetric = true
            }
            for (let property in playerUser) {
              if (
                playerUser[property] &&
                property !== "phone" &&
                property !== "parentPhone"
              ) {
                playerUserFormData.append(property, playerUser[property])
              }
            }

            if (this.playerUser.phone.number) {
              playerUserFormData.append(
                "phone.countryCode",
                this.countryCode.toUpperCase()
              )
              playerUserFormData.append(
                "phone.number",
                this.playerUser.phone.number
              )
            }

            if (this.parentPhoneNumber) {
              playerUserFormData.append(
                "parentPhone.countryCode",
                this.parentCountryCode.toUpperCase()
              )
              playerUserFormData.append(
                "parentPhone.number",
                this.parentPhoneNumber
              )
            }

            this.signUpAction({
              data: playerUserFormData,
              token: this.signUpToken,
            })
              .then((user) => {
                let toasterMsg = emailChanged
                  ? this.$t("OUTER_PAGES.signupPage.toastMessages.emailChanged")
                  : this.$t("OUTER_PAGES.signupPage.toastMessages.success")
                this.$eventHub.$emit(TOASTER_SUCCESS, { message: toasterMsg })
                this.formButtonLoading = false
                const userDetails = user.userDetails

                if (this.languageChangedOnSignUp) {
                  this.updateLanguageAction({
                    accountId: userDetails.accountId,
                    language: this.$i18n.locale.toUpperCase(),
                  }).then((response) => {
                    this.user.language = this.$i18n.locale.toUpperCase()
                    this.$router.push("/")
                  })
                } else {
                  this.$router.push("/")
                }

                // Additional mails checkup and sending from signup page
                if (!!this.additionalEmails.emails && user.userDetails.playerId) {
                  this.additionalEmails.emails.forEach(email => {
                    this.addAditionalEmailAction({
                      playerId: user.userDetails.playerId,
                      email
                    });
                  });
                }

                // Additional mails sending from signup page when parental consent is required
                if (this.parentalConsentNeeded) {
                  if (!!this.additionalEmails.emails && this.playerIdOnLoad) {
                    this.additionalEmails.emails.forEach(email => {
                      this.addAditionalEmailAction({
                        playerId: this.playerIdOnLoad,
                        email
                      });
                    });
                  }
                }
              })
              .catch((error) => {
                this.linkIsInvalid = true
                if (error.status.code === STATUS_CODES.BP_400) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.errorValidData')
                  });
                }else if (error.status.code === STATUS_CODES.BP_403) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.accountActivated')
                  });
                } else if (error.status.code === STATUS_CODES.BP_404) {
                  this.redeemedLink = false
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t("TOAST_MESSAGES.errorTryAgain")
                  });
                } else {
                  this.pageLoaded = true
                  context.$eventHub.$emit(TOASTER_ERROR, {
                    message: this.$t('TOAST_MESSAGES.otherError')
                  });
                }

                this.formButtonLoading = false
              })
          }
        }
      })
    },
    toggleLegalGuardianConfirmation() {
      this.playerUser.legalGuardianConfirmed = !this.playerUser
        .legalGuardianConfirmed
    },
    toggleTermsAndPolicesConfrimation() {
      this.playerUser.termsAndPolicyConfirmed = !this.playerUser
        .termsAndPolicyConfirmed
    },
    clearUpload() {
      this.playerUser.file = null
      this.files = []
      this.$refs.upload.clear()
    },
    getEmailInputs(inputs) {
      this.additionalEmails = inputs;
    },
    getPlayerIdOnLoad(id) {
      this.playerIdOnLoad = id;
    }
  },
  components: {
    FileUpload,
    Cropper,
    Datepicker,
    TagInput
  },
}
</script>

<style lang="scss">
@import "@/scss/views/_signup.scss";
.form-item-wrapper {
  &.imperial-wrapper {
    margin-top: 9px;
  }
  .imperial-fields {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 600px;
    .imperial-fields-input {
      width: 45%;
    }
    .imperial-fields-units {
      padding-top: 15px;
      margin-bottom: 0;
      color: $mid-gray;
    }
  }
  .change-metric {
    margin-right: 10px;
    margin-bottom: 8px;
    border: 1px solid #697b94;
    .btn {
      cursor: pointer;
    }
  }
  .form-help {
    width: 20px;
    float: right;
    img {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    .additional-mail-label {
      font-size: 14px;
    }
  }
}
</style>
